import React, { useMemo, useState } from 'react'
import {
  ArrowDownOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudOutlined,
  DeploymentUnitOutlined,
  DropboxOutlined,
  FlagOutlined,
  LeftOutlined,
  MobileOutlined,
  PushpinOutlined,
  RightOutlined,
  SwapOutlined,
  DesktopOutlined,
} from '@ant-design/icons'
import { Button, Modal, Space, Steps, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { useStorageMode } from '@contexts/operator/StorageModeContext'

const { Title, Paragraph, Text } = Typography

// 進倉三階段 — 垂直時間軸風格（取代 ASCII 流程）
// Stage key 與 AASM state 名稱對齊：pending / receiving / putting_away / done。
// AASM 多出的 `received`（驗收完、等上架的 transient state）不做獨立卡片，
// 標在 receiving → putting_away 的箭頭上。`canceled` 為正交 state，不在線性流程內，不顯示。
const INBOUND_STATUS_TO_ACTIVE = {
  pending:      { stage: 'pending' },
  receiving:    { stage: 'receiving' },
  received:     { after: 'receiving' },
  putting_away: { stage: 'putting_away' },
  done:         { stage: 'done' },
}

function OverviewDiagram({ t, prefix, active }) {
  const stages = [
    { key: 'pending', icon: <ClockCircleOutlined />, color: '#8c8c8c', bg: '#fafafa', border: '#e8e8e8' },
    { key: 'receiving', icon: <AuditOutlined />, color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
    { key: 'putting_away', icon: <DropboxOutlined />, color: '#fa8c16', bg: '#fff7e6', border: '#ffd591' },
    { key: 'done', icon: <CheckCircleOutlined />, color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff' },
  ]
  const hasActiveStage = Boolean(active?.stage)
  return (
    <div style={{ margin: '16px 0' }}>
      {stages.map((st, idx) => {
        const isStageActive = active?.stage === st.key
        const isTransitionActive = active?.after === st.key
        const nextStage = stages[idx + 1]
        return (
          <React.Fragment key={st.key}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: st.bg,
                border: isStageActive ? `2px solid ${st.color}` : `1px solid ${st.border}`,
                borderRadius: 6,
                padding: isStageActive ? '9px 13px' : '10px 14px',
                opacity: hasActiveStage && !isStageActive ? 0.55 : 1,
              }}
            >
              <span style={{ color: st.color, fontSize: 20, display: 'inline-flex' }}>{st.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: st.color, fontSize: 14, lineHeight: 1.4 }}>
                  {t(`${prefix}.ch1_stage_${st.key}_title`)}
                </div>
                <div style={{ color: '#595959', fontSize: 13, lineHeight: 1.5, marginTop: 2 }}>
                  {t(`${prefix}.ch1_stage_${st.key}_desc`)}
                </div>
              </div>
              {isStageActive && (
                <Tag color={st.color} style={{ margin: 0, fontWeight: 600 }}>
                  {t(`${prefix}.ch1_current_label`)}
                </Tag>
              )}
            </div>
            {nextStage && (
              <div style={{ textAlign: 'center', padding: '4px 0' }}>
                {isTransitionActive ? (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      background: nextStage.bg,
                      border: `2px solid ${nextStage.color}`,
                      borderRadius: 999,
                      padding: '2px 10px 2px 12px',
                      fontSize: 12,
                      color: nextStage.color,
                      fontWeight: 600,
                      lineHeight: 1.6,
                    }}
                  >
                    <ArrowDownOutlined />
                    <span>{t(`${prefix}.ch1_transition_after_${st.key}`)}</span>
                    <Tag color={nextStage.color} style={{ margin: 0 }}>
                      {t(`${prefix}.ch1_current_label`)}
                    </Tag>
                  </div>
                ) : (
                  <span style={{ color: '#bfbfbf', fontSize: 14, lineHeight: 1 }}>
                    <ArrowDownOutlined />
                  </span>
                )}
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

// 雙軌上架 — App vs Web 並排卡片比較（取代原本的樹狀 ASCII 圖）
function DualTrackDiagram({ t, prefix }) {
  const tracks = [
    {
      key: 'app',
      icon: <MobileOutlined style={{ fontSize: 20 }} />,
      accent: '#722ed1',
      headerBg: '#f9f0ff',
      border: '#d3adf7',
    },
    {
      key: 'web',
      icon: <DesktopOutlined style={{ fontSize: 20 }} />,
      accent: '#1890ff',
      headerBg: '#e6f7ff',
      border: '#91d5ff',
    },
  ]
  return (
    <div style={{ display: 'flex', gap: 12, margin: '16px 0', flexWrap: 'wrap' }}>
      {tracks.map((tr) => (
        <div
          key={tr.key}
          style={{
            flex: '1 1 220px',
            border: `1px solid ${tr.border}`,
            borderRadius: 8,
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          <div
            style={{
              background: tr.headerBg,
              padding: '10px 14px',
              borderBottom: `1px solid ${tr.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ color: tr.accent }}>{tr.icon}</span>
            <div>
              <div style={{ fontWeight: 600, color: tr.accent, fontSize: 14, lineHeight: 1.4 }}>
                {t(`${prefix}.dual_track_${tr.key}_title`)}
              </div>
              <div style={{ color: '#666', fontSize: 12, lineHeight: 1.4 }}>
                {t(`${prefix}.dual_track_${tr.key}_tagline`)}
              </div>
            </div>
          </div>
          <ol style={{ margin: 0, padding: '12px 14px 12px 30px', fontSize: 13, lineHeight: 1.9, color: '#333' }}>
            <li>{t(`${prefix}.dual_track_${tr.key}_step1`)}</li>
            <li>{t(`${prefix}.dual_track_${tr.key}_step2`)}</li>
            <li>{t(`${prefix}.dual_track_${tr.key}_step3`)}</li>
          </ol>
        </div>
      ))}
    </div>
  )
}

/**
 * 進倉流程說明（Operator / Show 頁）— 依倉庫 storage_mode 切換章節內容。
 *
 * 必須渲染在 OperatorStorageModeProvider 底下（透過 `useStorageMode()`）。
 *
 * @param {string} [currentStatus] - 當前 inbound.status（pending/receiving/received/putting_away/done/canceled）
 *                                   傳入後第一章會 highlight 對應的階段卡片。
 */
export default function OperatorInboundFlowConceptModal({ visible, onClose, currentStatus }) {
  const { t } = useTranslation()
  const { isVirtual, isDedicated, isDynamic } = useStorageMode()
  const [currentStep, setCurrentStep] = useState(0)

  const prefix = 'concepts.inbound_flow_operator'
  const active = INBOUND_STATUS_TO_ACTIVE[currentStatus]

  const chapters = useMemo(() => {
    const overview = {
      icon: <DeploymentUnitOutlined style={{ fontSize: 28, color: '#1890ff' }} />,
      titleKey: `${prefix}.ch1_title`,
      paragraphs: [`${prefix}.ch1_p1`],
      customDiagram: <OverviewDiagram t={t} prefix={prefix} active={active} />,
      paragraphsAfter: [`${prefix}.ch1_p2`],
    }

    const verify = {
      icon: <AuditOutlined style={{ fontSize: 28, color: '#52c41a' }} />,
      titleKey: `${prefix}.ch2_title`,
      paragraphs: [`${prefix}.ch2_p1`, `${prefix}.ch2_p2`],
      diagram: `${prefix}.ch2_diagram`,
      paragraphsAfter: [`${prefix}.ch2_p3`],
    }

    const virtualPutaway = {
      icon: <CloudOutlined style={{ fontSize: 28, color: '#13c2c2' }} />,
      titleKey: `${prefix}.virtual_title`,
      paragraphs: [`${prefix}.virtual_p1`, `${prefix}.virtual_p2`],
    }

    const dedicatedPutaway = {
      icon: <PushpinOutlined style={{ fontSize: 28, color: '#fa8c16' }} />,
      titleKey: `${prefix}.dedicated_title`,
      paragraphs: [`${prefix}.dedicated_p1`],
      diagram: `${prefix}.dedicated_diagram`,
      paragraphsAfter: [`${prefix}.dedicated_p2`],
    }

    const dualTrack = {
      icon: <SwapOutlined style={{ fontSize: 28, color: '#722ed1' }} />,
      titleKey: `${prefix}.dual_track_title`,
      paragraphs: [`${prefix}.dual_track_p1`],
      customDiagram: <DualTrackDiagram t={t} prefix={prefix} />,
      paragraphsAfter: [`${prefix}.dual_track_p2`],
    }

    const done = {
      icon: <FlagOutlined style={{ fontSize: 28, color: '#52c41a' }} />,
      titleKey: `${prefix}.done_title`,
      paragraphs: [`${prefix}.done_p1`],
      bullets: [`${prefix}.done_b1`, `${prefix}.done_b2`, `${prefix}.done_b3`],
    }

    const list = [overview, verify]
    if (isVirtual) {
      list.push(virtualPutaway)
    } else if (isDedicated) {
      list.push(dedicatedPutaway)
    } else if (isDynamic) {
      list.push(dualTrack)
    }
    list.push(done)
    return list
  }, [isVirtual, isDedicated, isDynamic, t, active])

  const chapter = chapters[currentStep] || chapters[0]
  const isFirst = currentStep === 0
  const isLast = currentStep === chapters.length - 1

  const handleClose = () => {
    setCurrentStep(0)
    onClose()
  }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      closable={false}
      width={720}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {currentStep + 1} / {chapters.length}
          </Text>
          <Space>
            {!isFirst && (
              <Button icon={<LeftOutlined />} onClick={() => setCurrentStep((s) => s - 1)}>
                {t(`${prefix}.prev`)}
              </Button>
            )}
            {isLast ? (
              <Button type="primary" onClick={handleClose}>
                {t(`${prefix}.got_it`)}
              </Button>
            ) : (
              <Button type="primary" onClick={() => setCurrentStep((s) => s + 1)}>
                {t(`${prefix}.next`)} <RightOutlined />
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Steps
        current={currentStep}
        size="small"
        onChange={(step) => setCurrentStep(step)}
        style={{ marginBottom: 32 }}
        items={chapters.map((ch, i) => ({
          title: i === currentStep ? t(ch.titleKey) : '',
        }))}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
        {chapter.icon}
        <Title level={3} style={{ margin: 0 }}>
          {t(chapter.titleKey)}
        </Title>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        {chapter.paragraphs?.map((key, i) => (
          <Paragraph key={i} style={{ fontSize: 15, lineHeight: 1.8, color: '#333' }}>
            {t(key)}
          </Paragraph>
        ))}

        {chapter.bullets && (
          <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
            {chapter.bullets.map((key, i) => (
              <li key={i} style={{ fontSize: 15, lineHeight: 2, color: '#333' }}>
                {t(key)}
              </li>
            ))}
          </ul>
        )}

        {chapter.customDiagram}

        {chapter.diagram && (
          <div
            style={{
              background: '#f6f8fa',
              border: '1px solid #e8e8e8',
              borderRadius: 8,
              padding: '20px 24px',
              margin: '16px 0',
              fontFamily: 'monospace',
              fontSize: 14,
              lineHeight: 1.8,
              whiteSpace: 'pre-line',
            }}
          >
            {t(chapter.diagram)}
          </div>
        )}

        {chapter.paragraphsAfter?.map((key, i) => (
          <Paragraph key={`after-${i}`} style={{ fontSize: 15, lineHeight: 1.8, color: '#333' }}>
            {t(key)}
          </Paragraph>
        ))}
      </div>
    </Modal>
  )
}
